import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, FileText, X } from 'lucide-react';
import {
  relatorioConsolidadoMultiUrl,
  relatorioConsolidadoUrl,
  useDatasVisita,
  useDatasVisitaMulti,
  useFazendaTalhoes,
} from '../api';

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

function iso(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function parseIso(s: string): Date {
  return new Date(`${s.slice(0, 10)}T00:00:00`);
}

// Miolo do seletor de data (toggle + calendário + seleção + botão gerar).
// Reutilizado na página full-screen (mobile) e no modal (desktop). Com
// `imovelIds` gera o consolidado de VÁRIAS fazendas (visita feita pela tela do
// produtor); com `imovelId`, o da fazenda.
function CalendarioPicker({
  imovelId,
  imovelIds,
  onGerar,
}: {
  imovelId?: number | undefined;
  imovelIds?: number[] | undefined;
  onGerar?: (() => void) | undefined;
}) {
  const multi = imovelIds != null && imovelIds.length > 0;
  const uma = useDatasVisita(multi ? null : (imovelId ?? null));
  const varias = useDatasVisitaMulti(multi ? imovelIds : []);
  const datas = multi ? varias.data : uma.data;

  const visitMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of datas ?? []) m.set(d.data.slice(0, 10), d.visitas);
    return m;
  }, [datas]);

  const hoje = new Date();
  const [mes, setMes] = useState({ y: hoje.getFullYear(), m: hoje.getMonth() });
  const [modo, setModo] = useState<'periodo' | 'dia'>('periodo');
  const [inicio, setInicio] = useState<string | null>(null);
  const [fim, setFim] = useState<string | null>(null);

  // Pula pro mês da visita mais recente quando os dados chegam.
  const jumped = useRef(false);
  useEffect(() => {
    if (jumped.current || !datas?.length) return;
    const last = parseIso(datas[datas.length - 1]!.data);
    setMes({ y: last.getFullYear(), m: last.getMonth() });
    jumped.current = true;
  }, [datas]);

  function clickDay(dayIso: string) {
    if (modo === 'dia') {
      setInicio(dayIso);
      setFim(dayIso);
      return;
    }
    if (!inicio || (inicio && fim)) {
      setInicio(dayIso);
      setFim(null);
    } else if (dayIso < inicio) {
      setInicio(dayIso);
      setFim(null);
    } else {
      setFim(dayIso);
    }
  }

  const visitasSel = useMemo(() => {
    if (!inicio) return 0;
    const a = inicio;
    const b = fim ?? inicio;
    let total = 0;
    for (const [k, v] of visitMap) if (k >= a && k <= b) total += v;
    return total;
  }, [inicio, fim, visitMap]);

  const primeiroDow = new Date(mes.y, mes.m, 1).getDay();
  const diasNoMes = new Date(mes.y, mes.m + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array<null>(primeiroDow).fill(null),
    ...Array.from({ length: diasNoMes }, (_, i) => i + 1),
  ];

  const prevMes = () => setMes((p) => (p.m === 0 ? { y: p.y - 1, m: 11 } : { y: p.y, m: p.m - 1 }));
  const nextMes = () => setMes((p) => (p.m === 11 ? { y: p.y + 1, m: 0 } : { y: p.y, m: p.m + 1 }));

  function fmtSel(): string {
    if (!inicio) return 'Nenhuma data selecionada';
    const a = parseIso(inicio);
    if (!fim || fim === inicio) return `${a.getDate()} de ${MESES[a.getMonth()]}`;
    const b = parseIso(fim);
    if (a.getMonth() === b.getMonth()) return `${a.getDate()} – ${b.getDate()} de ${MESES[b.getMonth()]}`;
    return `${a.getDate()} ${MESES[a.getMonth()]!.slice(0, 3)} – ${b.getDate()} ${MESES[b.getMonth()]!.slice(0, 3)}`;
  }

  function gerar() {
    if (!inicio) return;
    const url = multi
      ? relatorioConsolidadoMultiUrl(imovelIds, inicio, fim ?? inicio)
      : relatorioConsolidadoUrl(imovelId as number, inicio, fim ?? inicio);
    window.open(url, '_blank');
    onGerar?.();
  }

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 lg:px-5">
        {/* toggle período / dia */}
        <div className="mb-4 flex rounded-full bg-surface2 p-1">
          {(['periodo', 'dia'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                setModo(k);
                setInicio(null);
                setFim(null);
              }}
              className={`flex-1 rounded-full py-2 text-[0.85rem] font-semibold transition-colors ${
                modo === k ? 'bg-[var(--accent-100)] text-[var(--accent-900)]' : 'text-text-2'
              }`}
            >
              {k === 'periodo' ? 'Período' : 'Dia específico'}
            </button>
          ))}
        </div>

        {/* navegação de mês */}
        <div className="mb-2 flex items-center justify-between px-1">
          <button type="button" onClick={prevMes} className="flex size-8 items-center justify-center rounded-full text-text-2 hover:bg-surface2" aria-label="Mês anterior">
            <ChevronLeft size={18} strokeWidth={2} />
          </button>
          <span className="text-[0.95rem] font-semibold capitalize text-text">
            {MESES[mes.m]} {mes.y}
          </span>
          <button type="button" onClick={nextMes} className="flex size-8 items-center justify-center rounded-full text-text-2 hover:bg-surface2" aria-label="Próximo mês">
            <ChevronRight size={18} strokeWidth={2} />
          </button>
        </div>

        {/* grade do mês */}
        <div className="grid grid-cols-7 gap-y-1">
          {WEEKDAYS.map((w, i) => (
            <div key={i} className="py-1 text-center text-[0.72rem] font-medium text-text-muted">
              {w}
            </div>
          ))}
          {cells.map((d, i) => {
            if (d == null) return <div key={i} />;
            const k = iso(mes.y, mes.m, d);
            const temVisita = visitMap.has(k);
            const isInicio = k === inicio;
            const isFim = k === fim;
            const isEndpoint = isInicio || isFim;
            const col = i % 7;
            const inBand = !!inicio && !!fim && inicio !== fim && k >= inicio && k <= fim;
            // Faixa contínua: cada célula in-range pinta a largura toda; pontas e
            // limites de linha arredondam. Início/fim só pintam metade (sob o círculo).
            const bandCls = [
              'absolute inset-y-0 bg-[var(--accent-100)]',
              isInicio ? 'left-1/2' : 'left-0',
              isFim ? 'right-1/2' : 'right-0',
              isInicio || col === 0 ? 'rounded-l-full' : '',
              isFim || col === 6 ? 'rounded-r-full' : '',
            ].join(' ');
            return (
              <div key={i} className="relative flex h-9 items-center justify-center">
                {inBand && <div className={bandCls} />}
                <button
                  type="button"
                  onClick={() => clickDay(k)}
                  className={`relative flex size-9 items-center justify-center rounded-full text-[0.85rem] ${
                    isEndpoint ? 'bg-[var(--accent)] font-semibold text-[var(--on-solid)]' : 'text-text hover:bg-surface2'
                  }`}
                >
                  {d}
                  {temVisita && (
                    <span className={`absolute bottom-1 size-1 rounded-full ${isEndpoint ? 'bg-white' : 'bg-[var(--accent)]'}`} />
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-3 flex items-center gap-2 text-[0.78rem] text-text-2">
          <span className="size-2 rounded-full bg-[var(--accent)]" /> Dias com visita técnica
        </div>

        {/* seleção */}
        <div className="mt-4 rounded-[14px] border border-[var(--agro-card-border)] bg-surface2 px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 flex-col">
              <span className="text-[0.68rem] font-semibold uppercase tracking-wider text-text-muted">
                {modo === 'periodo' ? 'Período selecionado' : 'Dia selecionado'}
              </span>
              <span className="truncate text-[0.95rem] font-medium text-text">{fmtSel()}</span>
            </div>
            {inicio && (
              <span className="shrink-0 rounded-full bg-surface3 px-3 py-1 text-[0.78rem] font-medium text-text-2">
                {visitasSel} {visitasSel === 1 ? 'visita' : 'visitas'}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="shrink-0 border-t border-border bg-bg px-4 py-3 lg:px-5">
        <button
          type="button"
          onClick={gerar}
          disabled={!inicio}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--accent)] py-3.5 text-[0.95rem] font-semibold text-[var(--on-solid)] disabled:opacity-50"
        >
          <FileText size={18} strokeWidth={2} /> Gerar relatório
        </button>
      </div>
    </>
  );
}

// Tela 12 · Relatório consolidado (seletor de data) — página full-screen (mobile).
export function CalendarioRelatorio({ imovelId }: { imovelId: number }) {
  const { data: talhoes } = useFazendaTalhoes(imovelId);
  const fazenda = (talhoes ?? [])[0]?.fazenda ?? 'Fazenda';

  return (
    <div className="relative flex h-full flex-col bg-bg">
      <header className="flex flex-col px-3 pt-4 pb-3 shrink-0">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text hover:bg-surface2"
            aria-label="Voltar"
          >
            <ChevronLeft size={26} strokeWidth={2} />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-[1.35rem] font-bold leading-tight tracking-tight text-text">
            Relatório consolidado
          </h1>
        </div>
        <span className="pl-9 text-[0.85rem] text-text-2">{fazenda}</span>
      </header>

      <CalendarioPicker imovelId={imovelId} />
    </div>
  );
}

// Modal do seletor (desktop): sobe sobre a tela da fazenda, sem navegar.
export function CalendarioModal({
  imovelId,
  imovelIds,
  fazenda,
  onClose,
}: {
  imovelId?: number | undefined;
  imovelIds?: number[] | undefined;
  fazenda: string;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      // z acima do Leaflet (panes/controles até ~1000) pra o mapa não vazar sobre o modal.
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[86vh] w-full max-w-[460px] flex-col overflow-hidden rounded-[18px] border border-border bg-bg shadow-[var(--shadow-over)]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-center gap-2 border-b border-border px-5 py-4">
          <div className="flex min-w-0 flex-1 flex-col">
            <h2 className="truncate text-lg font-semibold leading-tight text-text">Relatório consolidado</h2>
            <span className="truncate text-[0.82rem] text-text-2">
              {fazenda}
              {imovelIds && imovelIds.length > 1 ? ` · ${imovelIds.length} fazendas` : ''}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex size-8 shrink-0 items-center justify-center rounded-full text-text-2 hover:bg-surface2 hover:text-text"
            aria-label="Fechar"
          >
            <X size={20} strokeWidth={2} />
          </button>
        </header>
        <CalendarioPicker imovelId={imovelId} imovelIds={imovelIds} onGerar={onClose} />
      </div>
    </div>
  );
}
